/**
 * Tabs, and the one of them that is not really a tab.
 *
 * # What a tab is
 *
 * **In-page state, not a second webview.** Tauri can put several webviews in one
 * window, and doing that here would have been the obvious reading of "tabs" —
 * but each webview is a separate process with its own JS context, so every tab
 * would carry its own copy of the store, its own session, its own event stream,
 * and would need cross-webview sync to agree with the others about what is
 * read. That is the duplication this client is built to avoid, and it buys an
 * isolation a mail client does not need: a tab's content is a message the app
 * is already holding.
 *
 * # A tab and a window are the same thing shown two ways
 *
 * Both are a view of `#/message/<account>/<id>`, which only became a sentence
 * anyone could write once messages had addresses. So **detaching is: close the
 * tab, open a window at that address**, and attaching is the reverse. Nothing
 * moves a live webview between windows — fortunate, because no platform this
 * client targets supports that.
 *
 * It also means a tab holds an *address*, never an `Envelope`. A tab that held
 * a copy of the message would be a second thing to keep in step with the store
 * every time a flag changed.
 *
 * # The first one is pinned
 *
 * Index 0 is the mail itself — the three panes, the list, everything. It cannot
 * be closed, detached or moved, and it is always leftmost. That is not a
 * decorative rule: it is the only view with a message list in it, so a window
 * that had lost it would be a mail client you cannot navigate, and "close the
 * last tab" would be a way to reach it by accident.
 */

import { batch, computed, effect, signal } from "@preact/signals";

import { TABS_KEY } from "./keys";
import { isPublicSession } from "./publicSession";

/** A message named the way every other part of this client names one. */
/** An attachment, named the way the daemon names it. */
export interface Part {
  blobId: string;
  name: string;
  type: string;
}

export interface Address {
  account: string;
  email: string;
  /**
   * The attachment this tab shows, when it shows one rather than the message.
   *
   * On the address rather than beside it, because an attachment tab *is*
   * addressed by a message and a part of it -- the same two things the
   * daemon's own blob id is made of. Everything that already reads
   * `account`/`email` keeps working unchanged, including the tab-to-store sync
   * in `lib/tabview.ts`: a tab showing a PDF is still a tab about that
   * message, and focusing it should still put the reader on it.
   */
  part?: Part;
  /**
   * The message's subject, captured when the tab was opened, so the tab bar can
   * label the tab without the message being in the current list.
   *
   * Not a contradiction of "a tab holds an address, never an `Envelope`": that
   * rule is about *mutable* state -- flags, read status -- which a tab must not
   * copy because it would then drift from the store. A subject does not change,
   * so a copy of it can never be stale, and the alternative is a tab that reads
   * "Message" the moment its message leaves the list (a folder switch, a
   * restart). `TabBar` still prefers the live row when there is one.
   */
  subject?: string;
}

export interface Tab {
  /** Stable for the life of the tab; the bar keys off it and so does focus. */
  id: string;
  /**
   * What this tab shows, or `null` for the pinned mail view.
   *
   * `null` is the mail rather than "nothing": exactly one tab has it, it is
   * always index 0, and {@link isPinned} is the only thing that should test for
   * it by hand.
   */
  address: Address | null;
}

/** The pinned tab's id. Fixed, because it is the same tab for the whole session. */
export const MAIN = "main";

export const tabs = signal<Tab[]>([{ id: MAIN, address: null }]);

/** Which tab is in front. Never points at a tab that is not in {@link tabs}. */
export const active = signal<string>(MAIN);

/** True while there is anything to show a bar for. One tab is not a tab bar. */
export const hasTabs = computed(() => tabs.value.length > 1);

/** The tab in front, which always exists. */
export const current = computed(
  () => tabs.value.find((t) => t.id === active.value) ?? tabs.value[0]!,
);

export function isPinned(tab: Tab): boolean {
  return tab.address === null;
}

let counter = 0;

/**
 * Show a message in a tab, focusing it.
 *
 * A message already open in a tab is **focused rather than opened twice**. Two
 * tabs of one message are two things to close and two places for a flag to
 * disagree, and somebody who asks for a message they already have open means
 * "show me it".
 */
export function open(address: Address): string {
  // Same message *and* same part: a message tab and a tab showing that
  // message's attachment are two different things to look at, and opening one
  // must not focus the other.
  const already = tabs.peek().find(
    (t) =>
      t.address?.account === address.account &&
      t.address.email === address.email &&
      t.address.part?.blobId === address.part?.blobId,
  );
  if (already) {
    active.value = already.id;
    return already.id;
  }
  counter += 1;
  const id = `tab-${counter}`;
  batch(() => {
    tabs.value = [...tabs.peek(), { id, address }];
    active.value = id;
  });
  return id;
}

/**
 * Close a tab. The pinned one refuses.
 *
 * Focus moves to the neighbour on the left rather than to the mail: closing the
 * third of four tabs and landing back at the inbox loses the place of somebody
 * working through several messages, and the left neighbour is the one that was
 * there before this tab existed.
 */
export function close(id: string): void {
  const list = tabs.peek();
  const at = list.findIndex((t) => t.id === id);
  if (at < 0) return;
  const tab = list[at]!;
  if (isPinned(tab)) return;

  const next = list.filter((t) => t.id !== id);
  batch(() => {
    tabs.value = next;
    if (active.peek() === id) active.value = (next[at - 1] ?? next[0]!).id;
  });
}

/**
 * Move a tab to a new index.
 *
 * Refuses to move the pinned tab and refuses to put anything before it. Both
 * halves matter and they are different rules: the first stops the mail being
 * dragged, the second stops it being displaced by something dragged past it.
 */
export function move(id: string, to: number): void {
  const list = tabs.peek();
  const from = list.findIndex((t) => t.id === id);
  if (from < 0) return;
  if (isPinned(list[from]!)) return;

  const target = Math.min(Math.max(to, 1), list.length - 1);
  if (target === from) return;

  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved!);
  tabs.value = next;
}

/**
 * Take a tab out of this window, handing its address to whoever opens windows.
 *
 * The tab is closed **only if the window was actually opened**. A detach that
 * failed and closed the tab anyway would lose the message from both places at
 * once, which is the one outcome worse than not detaching.
 */
export async function detach(id: string, toWindow: (address: Address) => Promise<void>): Promise<void> {
  const tab = tabs.peek().find((t) => t.id === id);
  if (!tab || isPinned(tab) || !tab.address) return;
  await toWindow(tab.address);
  close(id);
}

/**
 * Adopt a message that was in a window of its own.
 *
 * The other direction of {@link detach}, and it is an ordinary open: the window
 * closes itself, and what arrives here is an address like any other.
 */
export function attach(address: Address): string {
  return open(address);
}

/** Everything back to one pinned tab. For signing out. */
export function reset(): void {
  batch(() => {
    tabs.value = [{ id: MAIN, address: null }];
    active.value = MAIN;
  });
}

// ---- surviving a restart ----------------------------------------------------

// The key is in `lib/keys.ts` rather than here, because signing out clears it
// and `lib/auth.ts` has to be able to name it. See that file.

/**
 * Read back the tabs from last time, and keep writing them from now on.
 *
 * Called once at startup rather than run at import, so the tests above drive a
 * clean model and nothing here reads storage before the app has decided it
 * wants any.
 *
 * # Why the addresses and not the messages
 *
 * A tab holds an address, so restoring one is opening that address again — the
 * same path a link or a notification takes. Nothing about a restored tab is a
 * special case, and a message that has since been deleted fails the way any
 * other unreachable message does rather than by leaving a broken tab.
 *
 * # What is deliberately not trusted
 *
 * Everything. This is `localStorage`: it survives upgrades, it can be edited by
 * hand, and it may hold a shape this version has never written. So the pinned
 * tab is **rebuilt rather than read** — it is always first and always exists,
 * and storage does not get a say in that — and every restored entry has to look
 * like an address or it is dropped. A bad key costs somebody their tabs, which
 * is a small loss; a bad key that produced a window with no mail in it would
 * not be.
 */
export function restore(): void {
  let raw: string | null = null;
  // Nothing is read back into a public session. What is stored here is the
  // addresses of somebody's mail, and on a shared machine that somebody may not
  // be the person sitting in front of it -- see `lib/publicSession.ts`.
  if (!isPublicSession()) {
    try {
      raw = localStorage.getItem(TABS_KEY);
    } catch {
      // Private-browsing modes throw on access. No tabs is a fine outcome.
    }
  }

  if (raw !== null) {
    try {
      const saved: unknown = JSON.parse(raw);
      const list = (saved as { tabs?: unknown }).tabs;
      const wanted = (saved as { active?: unknown }).active;
      if (Array.isArray(list)) {
        const restored: Tab[] = [{ id: MAIN, address: null }];
        for (const entry of list) {
          const address = addressOf(entry);
          if (!address) continue;
          counter += 1;
          restored.push({ id: `tab-${counter}`, address });
        }
        batch(() => {
          tabs.value = restored;
          // Ids are minted fresh rather than restored, so the remembered active
          // tab is found by *address*. A stored id would have to be trusted not
          // to collide with one this session mints, which is a thing to get
          // wrong for no benefit.
          const at = restored.findIndex(
            (t) =>
              t.address &&
              typeof wanted === "object" &&
              wanted !== null &&
              t.address.account === (wanted as { account?: unknown }).account &&
              t.address.email === (wanted as { email?: unknown }).email,
          );
          active.value = at > 0 ? restored[at]!.id : MAIN;
        });
      }
    } catch {
      // Malformed. Start with the mail, which is always a valid state.
    }
  }

  effect(() => {
    // The address of what is in front, not its id: ids do not survive a restart
    // and nothing should depend on them doing so.
    const front = tabs.value.find((t) => t.id === active.value);
    const payload = {
      tabs: tabs.value.filter((t) => t.address).map((t) => t.address),
      active: front?.address ?? null,
    };
    // After the payload, not before it: building it is what subscribes this
    // effect to `tabs` and `active`, and a gate above that would stop this
    // effect ever running again.
    if (isPublicSession()) return;
    try {
      localStorage.setItem(TABS_KEY, JSON.stringify(payload));
    } catch {
      /* as above */
    }
  });
}

/** An address, if that is what this is. */
function addressOf(entry: unknown): Address | null {
  if (typeof entry !== "object" || entry === null) return null;
  const account = (entry as { account?: unknown }).account;
  const email = (entry as { email?: unknown }).email;
  if (typeof account !== "string" || typeof email !== "string") return null;
  if (account === "" || email === "") return null;
  const part = partOf((entry as { part?: unknown }).part);
  // A subject that was stored as anything but a string is simply dropped: the
  // tab falls back to the live row, then to "Message", both valid states.
  const rawSubject = (entry as { subject?: unknown }).subject;
  const subject = typeof rawSubject === "string" && rawSubject !== "" ? rawSubject : undefined;
  const base: Address = subject ? { account, email, subject } : { account, email };
  return part ? { ...base, part } : base;
}

/** The attachment on a stored tab, if it carried one and it is intact. */
function partOf(entry: unknown): Part | null {
  if (typeof entry !== "object" || entry === null) return null;
  const blobId = (entry as { blobId?: unknown }).blobId;
  const name = (entry as { name?: unknown }).name;
  const type = (entry as { type?: unknown }).type;
  if (typeof blobId !== "string" || blobId === "") return null;
  return {
    blobId,
    name: typeof name === "string" ? name : "attachment",
    type: typeof type === "string" ? type : "application/octet-stream",
  };
}
